'use client';

import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { InlineCreateStep } from './inline-create-step';
import { Label } from '@/components/ui/label';
import {
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_EDITOR_CHROME,
  ATTRIBUTE_HOVER,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LABEL,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_POPOVER_W,
  ATTRIBUTE_ROW,
  ATTRIBUTE_VALUE_INK,
  ATTRIBUTE_VALUE_TEXT,
  attributeLabelWidthStyle
} from '@/components/attributes';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/components/ui/command';
import { useSrnShares, type SrnShare } from '@/lib/services/srn-shares/hooks';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';

/**
 * Custom SRN share (not yet created in backend)
 * Used when user can't find existing ticker
 */
export interface CustomSrnShare {
  id: -1; // Special ID to indicate custom share
  ticker: string;
  companyName: string;
  isCustom: true;
}

/**
 * SRN Share value can be a complete SrnShare object, CustomSrnShare, or string (legacy format)
 */
export type SrnShareValue =
  | SrnShare
  | CustomSrnShare
  | string
  | null
  | undefined;

interface SrnShareSearchFieldProps {
  /** Current SRN share value (object or string) */
  value?: SrnShareValue;

  /** Callback when SRN share changes */
  onChange: (share: SrnShare | CustomSrnShare | string | null) => void;

  /** Display mode */
  mode: 'read' | 'edit';

  /** Whether field is disabled */
  disabled?: boolean;

  /**
   * Which chrome the field wears (VALE-355). Defaults to `form` — the look every
   * shipped SRN form has today, so adding this moves no existing pixel.
   *
   * `compact` is the attribute treatment, and this field was the last kept control
   * in the drawer without it: its sibling `HinShareSearchField` was converted, so
   * the SRN drawer showed a `text-sm` stacked label over a grey full-bleed trigger
   * beside rows in the treatment's own 13px label/value idiom. Reported as *"the
   * Ticker field looks like the old version of UI"*. The implementation is
   * deliberately its sibling's, down to the row wrapper.
   */
  variant?: 'form' | 'compact' | 'cell';

  /** Whether field is required */
  required?: boolean;
}

/**
 * Type guard to check if share is custom
 */
function _isCustomSrnShare(
  share: SrnShare | CustomSrnShare | null
): share is CustomSrnShare {
  return share !== null && 'isCustom' in share && share.isCustom === true;
}

/**
 * SRN share search field component
 *
 * Features:
 * - Read mode: displays ticker and company name
 * - Edit mode: searchable dropdown with backend fuzzy search
 * - Shows share registry (institution) in results
 * - Returns complete SrnShare object for auto-filling registry
 * - Handles both object and string formats
 */
export function SrnShareSearchField({
  value,
  onChange,
  mode,
  disabled = false,
  required = false,
  variant = 'form'
}: SrnShareSearchFieldProps) {
  /*
    `cell` is compact *styling* without compact's own row wrapper — so it must
    answer true here, or the field falls back to the form idiom and renders its
    own stacked `Label` inside a grid row that already has one.
  */
  const compact = variant === 'compact' || variant === 'cell';
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddNew, setShowAddNew] = useState(false);
  const [newTicker, setNewTicker] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');

  // Debounce the search term to avoid too many API calls
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  // Use React Query hook for fetching SRN shares
  const { data, isLoading } = useSrnShares({
    searchQuery: debouncedSearchTerm,
    page: 1,
    limit: 10
  });

  const shares = data?.items || [];

  // Parse current value to get SRN share
  const currentShare = useMemo(() => {
    if (!value) return null;

    // If it's already a SrnShare or CustomSrnShare object
    if (typeof value === 'object' && 'ticker' in value) {
      return value as SrnShare | CustomSrnShare;
    }

    // If it's a string, we can't find the full object without backend data
    return null;
  }, [value]);

  // Get string representation for display
  const displayValue = useMemo(() => {
    if (!value) return '';

    if (typeof value === 'object' && 'ticker' in value) {
      return `${value.ticker} - ${value.companyName}`;
    }

    if (typeof value === 'string') {
      return value;
    }

    return '';
  }, [value]);

  // Read-only mode
  if (mode === 'read') {
    if (!displayValue) return null;

    return (
      <div className="flex flex-col gap-1">
        <span className="text-muted-foreground text-sm font-medium">
          Company
        </span>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-foreground text-sm">{displayValue}</span>
        </div>
      </div>
    );
  }

  // Edit mode
  const handleSelect = (share: SrnShare | null) => {
    if (share) {
      onChange(share);
    } else {
      onChange(null);
    }
    setOpen(false);
    setSearchTerm('');
  };

  const handleAddNew = () => {
    if (!newTicker.trim() || !newCompanyName.trim()) return;

    // Create custom SRN share object
    const customShare: CustomSrnShare = {
      id: -1, // Special ID to indicate custom share
      ticker: newTicker.trim().toUpperCase(),
      companyName: newCompanyName.trim(),
      isCustom: true
    };

    onChange(customShare);

    // Reset state
    setShowAddNew(false);
    setNewTicker('');
    setNewCompanyName('');
  };

  const handleCancelAddNew = () => {
    setShowAddNew(false);
    setNewTicker('');
    setNewCompanyName('');
  };

  const body = (
    <div className={cn('flex min-w-0 flex-col gap-1', compact && 'flex-1')}>
      {/*
        In compact the label is the row's own column (the wrapper at the end of this
        component), so the field never renders a second one — a stacked `Label` above
        an attribute row is the mixed-idiom seam this variant exists to remove.
      */}
      {!compact && (
        <Label>
          Company / Ticker
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      {/* Searchable dropdown */}
      <Popover
        open={open}
        /*
          Dismissing the popover also leaves the create step — the same reset the
          institution picker needs, for the same reason: a `showAddNew` that
          survives a closed popover is a state nothing on screen can get out of.
        */
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setShowAddNew(false);
            setNewTicker('');
            setNewCompanyName('');
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={isLoading || disabled}
            className={cn(
              'bg-input-bg h-auto w-full justify-between rounded-md border-0 px-3 py-1.5 text-sm font-normal',
              /*
                The row's own inset and type, so an unset ticker reads as an empty
                attribute row rather than a control parked in one.
                `bg-transparent` from the bare token clears the trigger's own grey
                fill.
              */
              compact &&
                `${ATTRIBUTE_EDITOR_BARE} ${ATTRIBUTE_INSET} ${ATTRIBUTE_VALUE_TEXT} ${ATTRIBUTE_LEADING}`,
              /*
                **The ring and fill belong to the OPEN state only** (VALE-997).

                They were unconditional in compact, so this control sat in a box
                at rest while every value beside it sat in bare text — the box
                the reporter read as a card dropped into the form. Open, the
                chrome is correct and stays: it is then genuinely an editor. The
                institution picker beside it takes the identical treatment.
              */
              compact && (open ? ATTRIBUTE_EDITOR_CHROME : ATTRIBUTE_HOVER)
            )}
          >
            {currentShare ? (
              <div className="flex items-center gap-2 truncate">
                <span
                  className={cn(compact ? ATTRIBUTE_VALUE_INK : 'font-medium')}
                >
                  {currentShare.ticker}
                </span>
                <span
                  className={cn(
                    'truncate',
                    compact ? ATTRIBUTE_MUTED_INK : 'text-muted-foreground'
                  )}
                >
                  {currentShare.companyName}
                </span>
              </div>
            ) : displayValue ? (
              <span>{displayValue}</span>
            ) : (
              <span
                className={cn(
                  compact ? ATTRIBUTE_MUTED_INK : 'text-muted-foreground'
                )}
              >
                Search by company name or ticker...
              </span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className={`${ATTRIBUTE_POPOVER_W} p-0`} align="start">
          {showAddNew ? (
            <InlineCreateStep
              title="New ticker"
              fields={[
                {
                  label: 'Ticker symbol',
                  value: newTicker,
                  placeholder: 'e.g. BHP',
                  autoFocus: true,
                  onChange: setNewTicker
                },
                {
                  label: 'Company name',
                  value: newCompanyName,
                  placeholder: 'e.g. BHP Group Ltd',
                  onChange: setNewCompanyName
                }
              ]}
              hint="Both are required. The ticker is stored uppercase."
              submitLabel="Add ticker"
              onCancel={handleCancelAddNew}
              onSubmit={handleAddNew}
            />
          ) : (
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Enter ticker or company name..."
                value={searchTerm}
                onValueChange={setSearchTerm}
              />
              <CommandList>
                {isLoading ? (
                  <div className="text-muted-foreground p-4 text-center text-sm">
                    Searching...
                  </div>
                ) : shares.length === 0 && searchTerm.length >= 2 ? (
                  <CommandEmpty>No companies found.</CommandEmpty>
                ) : (
                  <CommandGroup>
                    <CommandItem
                      value="none"
                      onSelect={() => handleSelect(null)}
                    >
                      <span className="text-muted-foreground">None</span>
                    </CommandItem>
                    {shares.map((share) => (
                      <CommandItem
                        key={`${share.ticker}-${share.companyName}`}
                        value={`${share.ticker} ${share.companyName}`}
                        onSelect={() => handleSelect(share)}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Check
                            className={cn(
                              'h-4 w-4 shrink-0',
                              currentShare?.ticker === share.ticker
                                ? 'opacity-100'
                                : 'opacity-0'
                            )}
                          />
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {share.ticker}
                              </span>
                              <span className="text-muted-foreground text-sm">
                                {share.companyName}
                              </span>
                            </div>
                            {share.institution && (
                              <span className="text-muted-foreground text-xs">
                                Registry: {share.institution.name}
                              </span>
                            )}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {/*
                "Add new ticker" is the last item in the list, not a helper row
                under the field (LF-6314) — the same change made to
                `InstitutionSearchField`, applied here because this is the same
                pattern rather than a similar-looking one. Left as a helper row it
                wrapped at the inventory drawer's width and asked the question
                where the user is not looking: you only conclude the ticker is
                missing after searching, by which point you are inside the
                popover.
              */}
                {!disabled && (
                  <>
                    <CommandSeparator />
                    <CommandGroup>
                      <CommandItem
                        value="__add_new_ticker"
                        // Keeps the popover open and advances it to the create
                        // step — see `InlineCreateStep` for why.
                        onSelect={() => setShowAddNew(true)}
                        className="text-primary"
                      >
                        <Plus className="mr-2 h-4 w-4 shrink-0" />
                        Add a new ticker
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );

  /*
    **`cell` is the control with no row of its own** (VALE-863). `compact` wraps
    itself in a label column plus value, which is right when the field is its own
    block — but a field that sits *inside* the attribute grid is already in a row
    the grid drew, and wrapping again gave it a second label column and its own
    full-width line. That is what made a share drawer alternate between one field
    on a line and two, with the single ones stretched across the whole width for
    no reason.

    `body` is exactly the control, so the host row supplies the label and the
    value slot supplies the width.
  */
  if (variant === 'cell') return body;

  if (!compact) return body;

  /*
    **The compact field is a row, label column and all** — its sibling
    `HinShareSearchField`'s wrapper, verbatim in shape, including the one trap that
    file records: publish the label-width *variable* but **not** a new `@container`.
    `ATTRIBUTE_LABEL_WIDTH` is a container query, so declaring `@container` here would
    make this cell the ancestor it resolves against and the label column would come
    out a fraction of every other row's. The variable still has to be published,
    because `ATTRIBUTE_LABEL`'s `w-[var(--attr-label-w)]` is *invalid* when the
    property is undefined and the label then silently collapses to its text width.

    `Ticker` rather than `Company / Ticker`: the column is ~124px and the long form
    ellipsises. Nothing is lost — the value shows the ticker *and* the company name.
  */
  return (
    /*
      `@container` added (VALE-863): publishing the width is only half of it, and
      without a container of its own this row resolved `ATTRIBUTE_LABEL_WIDTH`
      against a wider ancestor — so `Ticker`'s value sat ~50px right of every
      other value in the section. Declared on this full-width wrapper it matches
      the grid's own container; the trap the comment above records is declaring
      one on the narrow cell instead.
    */
    <div className="@container" style={attributeLabelWidthStyle(2)}>
      <div className={ATTRIBUTE_ROW}>
        <span className={cn(ATTRIBUTE_LABEL, ATTRIBUTE_LEADING)} title="Ticker">
          Ticker
        </span>
        {body}
      </div>
    </div>
  );
}
